<template>
  <ArticleLayout :article="article">
    <div
      class="article-single__content-editor article-single__content-editor--no-border content-editor"
      :class="article.settings.cssClass"
      :style="{ 'font-family': article.settings.fontFamily }"
      @click="onDocContentClick"
    >
      <img class="article-single__cover" :src="article.cover_image" alt="" />
      <AnnotatorHost18170
        ref="rootElementRef"
        :content="article.page"
        @loaded="replaceLinks"
      >
        <div
          class="sf-chapter"
          oncopy="return false"
          @click="onBodyContentClick"
          v-html="articleHtml"
        ></div>
        <EOCButton
          v-if="article.isChapterEOC"
          text="Start Free Trial to Unlock"
          ios-text="Full Subscription Required to Read More"
        />
      </AnnotatorHost18170>
      <!--
      Continue Reading Foundations (#19038), variant B:
      the chapter is completed only after bottom of the chapter has been on the screen.
      -->
      <div
        v-on-visible.once="onChapterBottomSeen"
        class="sf-chapter__bottom"
      ></div>
    </div>
    <AudioPlayer
      v-if="article.audio && article.controls.isPlayerRendered"
      :doc="article"
    />
    <AudioPlayerDisabled
      v-if="
        !article.audio && article.has_audio && article.controls.isPlayerRendered
      "
      :class="article.controls.playerClass"
    />
  </ArticleLayout>
</template>

<script setup lang="ts">
import { onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import ArticleLayout from '@/app/components/article/Layout.vue'

import AudioPlayer from '@/app/components/doc/player/Player.vue'
import AudioPlayerDisabled from '@/app/components/doc/player/PlayerDisabled.vue'
import AnnotatorHost18170 from '@/app/components/doc/reading/AnnotatorHost18170.vue'
import EOCButton from '@/app/components/billing/EOCButton.vue'

import type { ArticleView } from '@/models/doc.article'
import { useRouter } from 'vue-router'
import { emitter } from '@/services/mitt'
import { formatDate } from '@/helpers/time'
import { parseTextForTranslation } from '@/services/weglot'
import { Mixpanel } from '@/analytics/mixpanel'
import { useInternalLinkReplacement } from '@/app/composables/useInternalLinkReplacement'
import { vOnVisible } from '@/helpers/directives'
import { miniPlayer19040Experiment } from '@/services/ab'
import { playerSession } from '@/services/player'

const props = defineProps<{
  article: ArticleView
}>()

const router = useRouter()

const rootElementRef = ref<InstanceType<typeof AnnotatorHost18170> | null>(null)
const { replaceLinks, cleanup } = useInternalLinkReplacement(rootElementRef)
const articleHtml = ref('')

onBeforeMount(async () => {
  Mixpanel.setFirstDoc('Viewed', props.article.title, props.article.doc_type)
  await miniPlayer19040Experiment()
})

/**
 * Hand over the player session from the Mini Player to the full player if the
 * it was playing the current page's doc. Otherwise, reset the player session.
 *
 * We watch the doc rather than hook the mount: the page keeps its component
 * when the reader moves from one doc to another.
 */
watch(
  () => props.article,
  (article: ArticleView) => {
    if (playerSession.value.isLoaded(article)) {
      // The doc was opened from the Mini Player. Open the full player.
      article.controls.showPlayer()
    } else {
      // Otherwise, just load the doc in the player session.
      playerSession.value.prepareDoc(article)
    }
  },
  { immediate: true },
)

onMounted(() => {
  articleHtml.value = getHtml()
})

onBeforeUnmount(async () => {
  cleanup()
})

function publishDate(): string {
  if (props.article.original_publish_date) {
    return formatDate(props.article.original_publish_date)
  }
  return ''
}

/**
 * We use the `getHtml` function instead of writing this directly since
 * 1) we want to use the v-html directive to render article.page.text
 * 2) the title, author, and blurb need to be annotatable, which means
 *    they need to be children of the `.sf-chapter` div.
 * See issue #4109.
 */
function getHtml(): string {
  // If the blurb exists, we add H3 after it, which renders the horizontal line.
  const blurbHtml =
    `<p>${props.article.blurb}</p>` + (props.article.blurb ? '<h3></h3>' : '')

  return `<div>
        <h2 class="article-single__title">${props.article.title}</h2>
        <p>
          <span class="article-single__author text-gray">
            By ${props.article.author}
            <span class="line"></span>
            <a
              href="${props.article.original_url}"
              target="_blank"
              class="article-single__publisher-link"
            >
              Published ${publishDate()} in
              <em>${props.article.publisher.name}</em></a
            >
            ${getSubscribeLink()}
            ${getDownloadLink()}
          </span>
        </p>
        ${blurbHtml}
     </div>
     ${parseTextForTranslation(props.article.page.text)}`
}

function getSubscribeLink(): string {
  if (!props.article.publisher.subscribe_url) {
    return ''
  }
  return `
      <span class="line"></span>
      <a
        href="${props.article.publisher.subscribe_url}"
        target="_blank"
      >Subscribe</a>`
}

function getDownloadLink(): string {
  const route = router.resolve({
    name: props.article.routeNames().export,
    params: { url_slug: props.article.url_slug },
  })

  return `
      <span class="line"></span>
      <a
        href="${route.href}"
        class="article-single__publisher-link"
      >Download as PDF</a>`
}

function onDocContentClick(event: any): void {
  emitter.emit('docContentClicked', event)
}

/**
 * Catch clicks on <a> for "Doc Link Click" event.
 */
function onBodyContentClick(event: any): void {
  if (event?.target.tagName === 'A') {
    const href = event.target.getAttribute('href')
    if (href) {
      Mixpanel.trackDocLinkClick(href, props.article)
    }
  }
}

/**
 * Report that the reader has reached the bottom of the chapter.
 */
function onChapterBottomSeen(): void {
  props.article.readingProgress?.markBottomSeen()
}
</script>

<style lang="scss" scoped>
.sf-chapter__bottom {
  // The directive needs 25% of the element to be visible, and the ratio of a
  // zero-height element is not worth relying on.
  height: 1px;
}
</style>
